import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listUsage, rollUsage } from "@/lib/usage.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Package, Truck, Tag, Sparkles, HardDrive } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/admin/billing")({ component: BillingAdmin });

const METRIC_META: Record<string, { label: string; icon: any; unit?: string }> = {
  orders: { label: "Orders", icon: Package },
  shipments: { label: "Shipments", icon: Truck },
  labels: { label: "Labels", icon: Tag },
  ai_calls: { label: "AI calls", icon: Sparkles },
  storage_gb_days: { label: "Storage", icon: HardDrive, unit: "GB·days" },
};

function BillingAdmin() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const lu = useServerFn(listUsage);
  const ru = useServerFn(rollUsage);

  const enabled = !!tenantId;
  const { data, isLoading } = useQuery({
    queryKey: ["admin-usage", tenantId],
    enabled,
    queryFn: () => lu({ data: { tenantId: tenantId!, months: 6 } }),
  });

  const byMonth = useMemo(() => {
    const map = new Map<string, Record<string, number>>();
    for (const r of data?.usage ?? []) {
      const m = (r as any).period_month as string;
      const metric = (r as any).metric as string;
      const val = Number((r as any).value ?? 0);
      if (!map.has(m)) map.set(m, {});
      map.get(m)![metric] = val;
    }
    return Array.from(map.entries()).sort(([a], [b]) => b.localeCompare(a));
  }, [data]);

  const current = byMonth[0]?.[1] ?? {};

  const onRoll = async () => {
    if (!enabled) return;
    await runServerFn(() => ru({ data: { tenantId: tenantId! } }), "Roll usage");
    toast.success("Usage rolled up");
    qc.invalidateQueries({ queryKey: ["admin-usage"] });
  };

  return (
    <>
      <PageHeader
        title="Billing & Usage"
        description="Per-tenant metering surfaced to billing. Roll up to refresh the current period."
        actions={
          <Button size="sm" variant="outline" onClick={onRoll} disabled={!enabled}>
            <RefreshCw className="h-4 w-4 mr-1" /> Roll current month
          </Button>
        }
      />

      <div className="px-4 sm:px-6 py-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {Object.entries(METRIC_META).map(([key, meta]) => {
          const Icon = meta.icon;
          const val = Number(current[key] ?? 0);
          return (
            <Card key={key}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon className="h-3.5 w-3.5" /> {meta.label}
                </div>
                <div className="mt-2 text-2xl font-semibold tabular-nums">
                  {val.toLocaleString()}
                  {meta.unit && <span className="text-xs text-muted-foreground ml-1">{meta.unit}</span>}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">This month</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="px-4 sm:px-6 pb-6">
        <Card>
          <CardHeader><CardTitle className="text-base">History (last 6 Months)</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {isLoading ? (
              <div className="p-6 text-sm text-center text-muted-foreground">Loading…</div>
            ) : byMonth.length === 0 ? (
              <div className="p-6 text-sm text-center text-muted-foreground">No usage recorded yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="text-left p-3">Period</th>
                    {Object.entries(METRIC_META).map(([k, m]) => (
                      <th key={k} className="text-right p-3">{m.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {byMonth.map(([month, vals], idx) => (
                    <tr key={month} className="border-t">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{month.slice(0, 7)}</span>
                          {idx === 0 && <Badge variant="outline" className="text-[10px]">Current</Badge>}
                        </div>
                      </td>
                      {Object.keys(METRIC_META).map((k) => (
                        <td key={k} className="p-3 text-right tabular-nums">
                          {Number(vals[k] ?? 0).toLocaleString()}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
